import { useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Phone, Mail, MapPin, ArrowRight } from "lucide-react";
import Reveal from "@/components/Reveal";
import useSEO from "@/hooks/useSEO";
import { COMPANY } from "@/data/site";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const SERVICE_OPTIONS = [
  "Drainage", "Groundworks", "Residential Landscaping", "Other",
];

export default function Contact() {
  useSEO("Contact");
  const [form, setForm] = useState({ full_name: "", email: "", phone: "", service: "", message: "" });
  const [submitting, setSubmitting] = useState(false);

  const onChange = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const onSubmit = async (e) => {
    e.preventDefault();
    if (!form.full_name || !form.email || !form.phone || !form.service || !form.message) {
      toast.error("Please fill in all fields.");
      return;
    }
    setSubmitting(true);
    try {
      await axios.post(`${API}/contact`, form);
      toast.success("Thanks! We'll be in touch shortly.");
      setForm({ full_name: "", email: "", phone: "", service: "", message: "" });
    } catch (err) {
      const detail = err?.response?.data?.detail;
      toast.error(typeof detail === "string" ? detail : "Something went wrong. Please try again.");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div data-testid="contact-page">
      {/* HERO */}
      <section className="bg-[#050505] pt-16 md:pt-24 pb-20 md:pb-28">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <Reveal><p className="cc-eyebrow justify-center mb-6">Contact Us</p></Reveal>
          <Reveal delay={100}>
            <h1 className="font-display font-black uppercase text-4xl sm:text-5xl lg:text-[60px] leading-[1.02] text-white">
              Get In Touch<br />
              With Our <span className="text-[#F28C00]">Team Today</span>
            </h1>
          </Reveal>
          <Reveal delay={200}>
            <p className="text-[#9A9A9A] text-base sm:text-lg leading-relaxed mt-8 max-w-2xl mx-auto">
              Loyalty, shared values and a strong work ethic are at the heart of everything we do. Whether you're planning a driveway, a full back garden makeover, or a complex commercial groundworks project — we'd love to hear about it.
            </p>
          </Reveal>
        </div>
      </section>

      {/* CONTACT CARDS */}
      <section className="bg-[#0B0B0B] py-20 md:py-24 border-y border-[#181818]">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 grid md:grid-cols-3 gap-6 cc-stagger">
          {[
            { icon: Phone, label: "Call Us", value: COMPANY.phone, href: `tel:${COMPANY.phone.replace(/\s/g, "")}`, testId: "contact-card-phone" },
            { icon: Mail, label: "Send Us An Email", value: COMPANY.email, href: `mailto:${COMPANY.email}`, testId: "contact-card-email" },
            { icon: MapPin, label: "Visit Our Office", value: COMPANY.addressLine, testId: "contact-card-address" },
          ].map((c) => (
            <Reveal key={c.label} className="bg-[#111111] border border-[#181818] p-8 hover:border-[#F28C00]/60 transition-colors" data-testid={c.testId}>
              <div className="w-12 h-12 inline-flex items-center justify-center bg-[#F28C00] text-white mb-6">
                <c.icon className="w-5 h-5" />
              </div>
              <h3 className="font-display font-bold uppercase text-white text-sm tracking-[0.18em] mb-3">{c.label}</h3>
              {c.href ? (
                <a href={c.href} className="text-[#9A9A9A] hover:text-[#F28C00] transition-colors text-base leading-relaxed break-words">{c.value}</a>
              ) : (
                <p className="text-[#9A9A9A] text-base leading-relaxed">{c.value}</p>
              )}
            </Reveal>
          ))}
        </div>
      </section>

      {/* CONTACT FORM */}
      <section className="bg-[#050505] py-24 md:py-32">
        <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <Reveal><p className="cc-eyebrow justify-center mb-6">Send A Message</p></Reveal>
            <Reveal delay={100}>
              <h2 className="font-display font-black uppercase text-3xl sm:text-4xl lg:text-5xl text-white leading-[1.05]">
                Tell Us About Your Project
              </h2>
            </Reveal>
          </div>

          <Reveal delay={200}>
            <form onSubmit={onSubmit} className="space-y-5" data-testid="contact-form">
              <div className="grid sm:grid-cols-2 gap-5">
                <input
                  type="text"
                  placeholder="Full Name"
                  className="cc-input"
                  value={form.full_name}
                  onChange={onChange("full_name")}
                  data-testid="contact-input-name"
                  required
                />
                <input
                  type="email"
                  placeholder="Email Address"
                  className="cc-input"
                  value={form.email}
                  onChange={onChange("email")}
                  data-testid="contact-input-email"
                  required
                />
              </div>
              <div className="grid sm:grid-cols-2 gap-5">
                <input
                  type="tel"
                  placeholder="Phone Number"
                  className="cc-input"
                  value={form.phone}
                  onChange={onChange("phone")}
                  data-testid="contact-input-phone"
                  required
                />
                <select
                  className="cc-input"
                  value={form.service}
                  onChange={onChange("service")}
                  data-testid="contact-input-service"
                  required
                >
                  <option value="">Service Interested In</option>
                  {SERVICE_OPTIONS.map((s) => (
                    <option key={s} value={s}>{s}</option>
                  ))}
                </select>
              </div>
              <textarea
                placeholder="Tell us about your project..."
                className="cc-input"
                rows={6}
                value={form.message}
                onChange={onChange("message")}
                data-testid="contact-input-message"
                required
              />
              <div className="pt-4">
                <button
                  type="submit"
                  disabled={submitting}
                  className="cc-btn-primary w-full sm:w-auto disabled:opacity-60"
                  data-testid="contact-submit"
                >
                  {submitting ? "Sending..." : "Submit Message"} <ArrowRight className="w-4 h-4" />
                </button>
              </div>
            </form>
          </Reveal>
        </div>
      </section>
    </div>
  );
}
